/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useRouter } from 'next/router'

import { useUpdateBookShelf } from '@hooks/myReading/useUpdateBookShelf'
import { useGetBookShelfStatus } from '@hooks/myReading/useGetBookShelfStatus'
import { UserReadingFormLocalStorageType } from '@interfaces/UserReadingFormLocalStorageType'
import { useAuthentication } from '@hooks/useAuthentication'
import { useStoryReader } from '@hooks/book/useStoryReader'
import { BASE_URL } from '@configs/config'
import { useAlert } from '@hooks/useAlert'
import { BookReportFormType } from '@models/report/BookReportFormType'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useUserReadingAction } from '@hooks/userReading/useUserReadingAction'
import { SearchByEnum } from '@interfaces/SearchByEnum'
import { useTagAction } from '@hooks/tag/useTagAction'
import { useReportAction } from '@hooks/report/useReportAction'
import { BookShelfStatusType } from '@models/book/BookShelfStatusType'
import { ReportBookType } from '@interfaces/ReportBookType'
import { CoverProps } from './interface'

export function withCover(Component: React.FC<CoverProps>) {
  function WithCover() {
    const queryClient = useQueryClient()
    const reportClient = useReportAction()
    const userReadingClient = useUserReadingAction()
    const tagClient = useTagAction()
    const alert = useAlert()
    const router = useRouter()
    const { isAuthenticated, user } = useAuthentication()
    const {
      data,
      bookId,
      isToggleFollowPenNameLoading,
      isFollowedPenName,
      isLodingToggleBookLike,
      continueToRead,
      handleLikeBook,
      handleFollowPenName,
    } = useStoryReader()

    const reportModal = useModal(
      {
        modal: 'reportModal',
        modalProps: {
          onSubmit: bookReportHandler,
        },
      },
      true
    )

    const loginModal = useModal({ modal: 'login' })

    const { mutate: createUserTagClick } = useMutation((tagsId: number) =>
      tagClient.createUserTagClick({
        userId: isAuthenticated ? user.id : undefined,
        tagsId,
        categoriesId: data!.category.id,
      })
    )

    function handleGoToSearchTagPage(tagName: string, tagId: number) {
      createUserTagClick(tagId)
      router.push({
        pathname: '/search/story',
        query: {
          text: encodeURIComponent(tagName),
          tab: SearchByEnum.TAG,
        },
      })
    }

    const { data: statusShelfData, refetch: refetchStatusShelf } =
      useGetBookShelfStatus(data?.id || 0)

    const { mutate: updateBookShelf, isLoading: isBookShelfActionLoading } =
      useUpdateBookShelf(
        (_, variables) => {
          if (variables.action === 'ADD') alert.success('เพิ่มเข้าชั้นสำเร็จ')
          else alert.success('ลบหนังสือออกจากชั้นสำเร็จ')

          queryClient.setQueryData<BookShelfStatusType | undefined>(
            ['status-shelf', bookId],
            oldData => {
              if (oldData) {
                return {
                  isBookOnShelf: variables.action === 'ADD',
                }
              }

              return oldData
            }
          )

          refetchStatusShelf()
        },
        () => {
          alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
        }
      )

    function openReportModal() {
      if (isAuthenticated) {
        reportModal.show()
      } else {
        loginModal.show()
      }
    }

    async function bookReportHandler(val: BookReportFormType) {
      try {
        if (data?.id) {
          await reportClient.createBookReport(
            val.title,
            val.detail,
            data.id,
            ReportBookType.STORY
          )
          alert.success('รายงานเนื้อหาสำเร็จ')
        }
      } catch (_) {
        alert.error('เกิดข้อผิดพลาด ไม่สามารถรายงานเนื้อหาได้')
      } finally {
        reportModal.hide()
      }
    }

    async function readNowHandler() {
      if (isAuthenticated) {
        try {
          const readNow = await userReadingClient.readNow(bookId)
          router.push(`/story/${bookId}/chapter/${readNow?.chapterId}`)
        } catch (err: any) {
          if (err.response.errors[0].statusCode === 404 && continueToRead) {
            router.push(`/story/${bookId}/chapter/${continueToRead.id}`)
          } else alert.error('เกิดข้อผิดพลาด ไม่สามารถเข้าถึงเนื้อหาได้')
        }
      }

      const userReading = await userReadingClient.getGuestReading(true)

      if (userReading.length && !isAuthenticated) {
        const continueBook = userReading.filter(
          (localData: UserReadingFormLocalStorageType) =>
            localData.bookId === bookId
        )

        if (continueBook.length > 0) {
          router.push(
            `/story/${bookId}/chapter/${
              continueBook[continueBook.length - 1].chapterId
            }`
          )
        } else if (continueToRead) {
          router.push(`/story/${bookId}/chapter/${continueToRead.id}`)
        }
      }

      if (!userReading && !isAuthenticated && continueToRead) {
        router.push(`/story/${bookId}/chapter/${continueToRead.id}`)
      }
    }

    function handleUpdateBookShelf(id: number, action: string) {
      if (isAuthenticated) {
        updateBookShelf({ bookId: id, action })
      } else {
        loginModal.show()
      }
    }

    function goToEbookSeries() {
      router.push(`/story/${data?.id}/ebook`)
    }

    if (!data) return null

    const newProps = {
      id: data.id,
      shareUrl: `${BASE_URL}/story/${data.id}`,
      coverImgPath: data.coverImgPath,
      title: data.title,
      bookType: data.bookType,
      writingType: data.writingType,
      publishedAt: data.publishedAt,
      cover: data.cover,
      viewCount: data.viewCount,
      commentCount: data.commentCount,
      likeCount: data.likeCount,
      chapterTotal: data.publishedChapterCount,
      penName: data.penName,
      tags: data.tags,
      category: data.category,
      ratingName: data.rating.name,
      writer: data.writer,
      isTranslated: data.isTranslated,
      isEnded: data.isEnded,
      isEpub: data.isEpub,
      isPdf: data.isPdf,
      isLiked: data.isLiked,
      isBookOnShelf: statusShelfData?.isBookOnShelf || false,
      totalEbookSeriesPublish: data.totalEbookSeriesPublish,
      isFollowedPenName,
      isToggleFollowPenNameLoading,
      canFollow: data.penName.user.id !== user.id,
      isLodingToggleBookLike,
      isShowBookPromotion: data.hasSalePromotions,
      buyAllChaptersPromotion: data.buyAllChaptersPromotion,
      readFreePromotion: data.readFreePromotion,
      bookBadges: data.bookBadges,
      handleLikeBook,
      handleUpdateBookShelf,
      isBookShelfActionLoading,
      openReportModal,
      readNowHandler,
      handleFollowPenName,
      handleGoToSearchTagPage,
      goToEbookSeries,
    }

    return <Component {...newProps} />
  }

  return WithCover
}
